// File: frontend/src/components/league/LeagueLayout.tsx
import React, { useEffect } from 'react';
import { Navigate, NavLink, useParams, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Spinner from '@/components/common/Spinner';
import LeagueErrorState from '@/components/league/LeagueErrorState';
import { useLeagueDetails } from '@/hooks/useLeagueDetails';
import { isLeaguePreStart } from '@/utils/leagueLifecycle';
import { isKnownLeagueRoutePath } from '@/utils/leagueNavigation';

// === NEW IMPORTS ===
import { useQuery } from '@tanstack/react-query';
import { fetchUnreadActivityCount } from '@/api/activity';
// ===================

// Icons for the mobile bottom league nav, keyed by navItem.key.
const svg = (d: string) => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={d} />
  </svg>
);
const NAV_ICONS: Record<string, React.ReactNode> = {
  setup: svg("M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"),
  'my-team': svg("M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"),
  matchup: svg("M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"),
  league: svg("M3 3v18h18M8 17V9m4 8V5m4 12v-6"),
  players: svg("M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"),
};

const LeagueLayout: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { user, token } = useAuth(); // Get token for API calls
  const navigate = useNavigate();
  const location = useLocation();
  const numericLeagueId = leagueId ? parseInt(leagueId, 10) : null;
  const normalizedPathname = location.pathname.replace(/\/+$/, '') || '/';
  const isOnCommishHQ = normalizedPathname === `/leagues/${numericLeagueId}/commish`;
  // The member's read-only view of the same page. Deliberately NOT folded into
  // isOnCommishHQ, which gates the member redirect below: these are the two
  // halves of one mode, but only one of them is commissioner-only.
  const isOnLeagueSettings = normalizedPathname === `/leagues/${numericLeagueId}/settings`;
  const isOnLeagueMode = isOnCommishHQ || isOnLeagueSettings;

  const {
    data: leagueDetails,
    isLoading: isLoadingLeagueLayout,
    isError,
    error: leagueDetailsError,
  } = useLeagueDetails(numericLeagueId);

  // === NEW: FETCH UNREAD COUNT ===
  const { data: unreadData } = useQuery({
      queryKey: ['unreadActivityCount', numericLeagueId],
      queryFn: () => fetchUnreadActivityCount(numericLeagueId!, token!),
      enabled: !!numericLeagueId && !!token,
      refetchInterval: 30000, // Refetch every 30 seconds to keep the badge updated
      refetchOnWindowFocus: true,
  });
  const unreadCount = (unreadData?.unread_count || 0) + (unreadData?.new_players_unread_count || 0);
  // =============================

  // Pre-start gating. Commissioner gets bounced to /setup so they can finish
  // configuring the draft; everyone else is bounced to /my-team (which shows
  // a draft countdown card in place of the matchup card). League members
  // shouldn't see the commissioner's setup screens.
  useEffect(() => {
    if (leagueDetails && user) {
      // Mock isolation is enforced during render below, before real-league
      // chrome can paint. It never participates in the ordinary route gate.
      if (leagueDetails.is_mock) return;

      const isPreStartPhase = isLeaguePreStart(leagueDetails.draft_status);
      const isCommissioner = leagueDetails.owner_id === user.id;

      const isOnSetupOrAdminPage =
        location.pathname.endsWith(`/leagues/${numericLeagueId}/setup`) ||
        location.pathname.startsWith(`/leagues/${numericLeagueId}/admin`) ||
        isOnCommishHQ;
      const isOnMyTeamPage = location.pathname.includes(`/leagues/${numericLeagueId}/my-team`);
      const isOnDraftPage = location.pathname.endsWith(`/leagues/${numericLeagueId}/draft`);

      // Unknown children belong to the nested 404. Lifecycle gates only own
      // real league pages; otherwise a typo would silently become Setup or
      // My Team for a pre-start league.
      if (!leagueId || !isKnownLeagueRoutePath(location.pathname, leagueId)) return;

      if (!isPreStartPhase) return;

      // Until the live draft has a scheduled TIME, the commissioner's FIRST
      // entry into the league each session lands on League Setup - whatever
      // URL they arrived on (deep link, login restore, dashboard). After
      // that one routing they navigate freely: My Team stays reachable (the
      // pre-draft research board lives there), and hijacking a deliberate
      // nav tap reads as a bug. Owner call 2026-07-13, closing the hole in
      // the 2026-07-10 dashboard-entry routing.
      const draftUnscheduled =
        leagueDetails.draft_type === 'SNAKE' &&
        leagueDetails.draft_status === 'PRE_DRAFT' &&
        !leagueDetails.draft_scheduled_at;
      const setupRoutedKey = `ff-setup-routed-${numericLeagueId}`;
      let setupAlreadyRouted = false;
      try { setupAlreadyRouted = sessionStorage.getItem(setupRoutedKey) === '1'; } catch { /* private mode */ }
      const markSetupRouted = () => {
        try { sessionStorage.setItem(setupRoutedKey, '1'); } catch { /* private mode */ }
      };

      if (isCommissioner) {
        if (draftUnscheduled && isOnSetupOrAdminPage) {
          markSetupRouted(); // arriving on setup directly counts as the landing
        }
        if (draftUnscheduled && !setupAlreadyRouted && !isOnSetupOrAdminPage && !isOnDraftPage) {
          markSetupRouted();
          navigate(`/leagues/${numericLeagueId}/setup`, { replace: true });
        } else if (!isOnSetupOrAdminPage && !isOnMyTeamPage && !isOnDraftPage) {
          // Standing pre-start gate: pages that are empty shells before the
          // season (standings, matchups, ...) always route to setup.
          navigate(`/leagues/${numericLeagueId}/setup`, { replace: true });
        }
      } else {
        // Member: never see setup/admin pre-start; anywhere else -> my-team.
        if (isOnSetupOrAdminPage) {
          navigate(`/leagues/${numericLeagueId}/my-team`, { replace: true });
        } else if (!isOnMyTeamPage && !isOnDraftPage) {
          navigate(`/leagues/${numericLeagueId}/my-team`, { replace: true });
        }
      }
    }
  }, [leagueDetails, user, navigate, location.pathname, numericLeagueId, leagueId, isOnCommishHQ]);

  // Draft room is a full-screen experience: render it WITHOUT the league
  // header/sub-nav chrome (and don't block it on league-details loading).
  const isDraftRoom =
    !!numericLeagueId &&
    location.pathname.endsWith(`/leagues/${numericLeagueId}/draft`);
  if (isDraftRoom) {
    return <Outlet />;
  }

  // EARLY RETURNS FOR LOADING/ERROR STATES
  if (isLoadingLeagueLayout) {
    return (
      <div className="flex flex-col justify-center items-center min-h-[calc(100vh-150px)] bg-primary p-4">
        <Spinner size="lg" color="text-accent-yellow" />
        <p className="mt-2 text-gray-300">Loading League...</p>
      </div>
    );
  }
  
  if (isError) {
    return <LeagueErrorState message={leagueDetailsError?.message} />;
  }

  if (!leagueDetails) {
    return <LeagueErrorState message="League data not found or you may not have access." />;
  }

  // A mock's only surface is the full-screen draft room. Render-time routing
  // prevents even one frame of real-league chrome around bad mock URLs.
  if (leagueDetails.is_mock) {
    return <Navigate to={`/leagues/${numericLeagueId}/draft`} replace />;
  }

  // NOW IT'S SAFE TO ACCESS leagueDetails PROPERTIES
  const isPreStartPhase = isLeaguePreStart(leagueDetails.draft_status);
  const isCommissioner = !!user && leagueDetails.owner_id === user.id;

  // The route is deliberately absent from member navigation, but the render
  // gate prevents a typed URL from flashing commissioner controls. The API's
  // commissioner check remains the authoritative security boundary.
  if (isOnCommishHQ && !isCommissioner) {
    return (
      <Navigate
        to={isPreStartPhase
          ? `/leagues/${numericLeagueId}/my-team`
          : `/leagues/${numericLeagueId}/league`}
        replace
      />
    );
  }

  const navLinkBaseClass = "px-3 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap";
  const navLinkActiveClass = "bg-accent-yellow text-primary shadow-sm";
  const navLinkInactiveClass = "text-gray-300 hover:bg-primary-dark hover:text-accent-yellow active:bg-primary-dark active:text-accent-yellow";

  // Nav composition:
  //  - Pre-start commissioner: Lobby + My Team + Players (they're configuring the draft)
  //  - Pre-start member: just My Team (countdown card lives there)
  //  - Post-start (everyone): My Team + Matchup + League + Players
  const navItems: { path: string; label: string; key: string }[] = [];
  if (isPreStartPhase) {
    if (isCommissioner) {
      navItems.push({ path: `/leagues/${numericLeagueId}/setup`, label: "League Setup", key: 'setup' });
    }
    navItems.push({ path: `/leagues/${numericLeagueId}/my-team`, label: "My Team", key: 'my-team' });
    if (isCommissioner) {
      navItems.push({ path: `/leagues/${numericLeagueId}/players`, label: "Players", key: 'players' });
    }
  } else {
    navItems.push({ path: `/leagues/${numericLeagueId}/my-team`, label: "My Team", key: 'my-team' });
    navItems.push({ path: `/leagues/${numericLeagueId}/matchup`, label: "Matchup", key: 'matchup' });
    navItems.push({ path: `/leagues/${numericLeagueId}/league`, label: "League", key: 'league' });
    navItems.push({ path: `/leagues/${numericLeagueId}/players`, label: "Players", key: 'players' });
  }

  // On the setup lobby the sticky LaunchBar owns the bottom, so keep the tabs on
  // top there (mobile + desktop). Everywhere else: tabs are the bottom bar on
  // mobile, the top sub-nav on desktop.
  const isOnSetup = location.pathname.endsWith('/setup');

  return (
    <div className="flex flex-col h-full"> {/* ADD h-full to fill parent */}
      {/* No league header row (owner call 2026-07-10): the league name only
          identifies a league where you CHOOSE one (dashboard) or on the
          League home page - in-league it was pure chrome. Desktop nav lives
          in the GLOBAL top bar (LeagueTopTabs); mobile uses the bottom bar.
          The one exception: the setup lobby below lg, where the LaunchBar
          owns the bottom slot, keeps a slim top tab row. */}
      {isOnSetup && (
        <header className="bg-primary shadow-md sticky top-[var(--app-mobile-header-offset)] z-30 border-b-2 border-primary-dark flex-shrink-0 lg:hidden">
          <div className="container mx-auto px-4 py-2">
            <nav className="flex flex-wrap justify-center sm:justify-start items-center gap-x-2 gap-y-1 overflow-x-auto scrollbar-mobile-hide pb-1">
              {navItems.map(item => (
                <div key={item.key} className="relative">
                  <NavLink to={item.path} className={({ isActive }) => {
                    const isModeActive = isActive || (
                      isOnLeagueMode && (
                        (isPreStartPhase && item.key === 'setup')
                        || (!isPreStartPhase && item.key === 'league')
                      )
                    );
                    return `${navLinkBaseClass} ${isModeActive ? navLinkActiveClass : navLinkInactiveClass}`;
                  }}>
                    {item.label}
                  </NavLink>
                  {item.key === 'league' && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-white text-badge font-bold pointer-events-none">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </header>
      )}
      {/* Tight top: the global bar sits directly above content now, so
          vertical padding is minimal; horizontal keeps the page gutters.
          pb-24 clears the mobile bottom nav; desktop has none. */}
      <main className={`flex-grow overflow-y-auto px-4 pt-2 md:px-6 md:pt-3 lg:px-8 lg:pb-6 ${isOnSetup ? 'pb-24' : 'pb-[calc(var(--app-mobile-bottom-nav-height)+0.5rem)]'}`}>
        <Outlet />
      </main>

      {/* Contextual league bottom nav (MOBILE ONLY - desktop gets the top
          sub-nav in the header). Hidden on the setup lobby (LaunchBar owns
          the bottom) AND when there is only one destination - a single-item
          nav is a giant button to nowhere (pre-start members have just My
          Team). */}
      {!isOnSetup && navItems.length > 1 && (
        <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-primary border-t border-gray-700/50 shadow-lg z-40 pb-[env(safe-area-inset-bottom)]">
          <div className="mx-auto flex h-[3.25rem] max-w-md items-center justify-around">
            {navItems.map(item => (
              <NavLink
                key={item.key}
                // A red League badge opens the Activity panel, where the
                // count continues, instead of Standings.
                to={item.key === 'league' && unreadCount > 0 ? `${item.path}?tab=activity` : item.path}
                className={({ isActive }) => {
                  const isModeActive = isActive || (
                    isOnLeagueMode && (
                      (isPreStartPhase && item.key === 'setup')
                      || (!isPreStartPhase && item.key === 'league')
                    )
                  );
                  return `relative flex flex-col items-center justify-center w-full pt-1.5 pb-1 mx-0.5 rounded-lg transition-colors duration-150 active:opacity-70 ${isModeActive ? 'bg-accent-yellow text-primary font-bold' : 'text-gray-400'}`;
                }}
              >
                {item.key === 'league' && unreadCount > 0 && (
                  <span className="absolute top-0 right-3 flex h-4 w-4 items-center justify-center rounded-full bg-secondary text-white text-badge font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
                {NAV_ICONS[item.key] ?? null}
                <span className="text-badge font-medium mt-1">{item.label}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </div>
  );
};

export default LeagueLayout;
